import { Product, queryReplaceParam, Variant } from '@process-creative/slate-theme-tools';

const SEL_ELEMENT_DATA = '[data-variant-selector-data]';

type VariantSelectorContainer = HTMLInputElement | HTMLSelectElement;

export const EVT_VARIANT_CHANGED = 'onVariantChange';
export class EventVariantChanged extends CustomEvent<{
  variantSelector:VariantSelector,
  oldVariant:Variant | null,
  newVariant:Variant | null
}> {
}

export class VariantSelector {
  public readonly container:HTMLElement;
  public readonly variantSelector:VariantSelectorContainer;
  private readonly elementData:HTMLScriptElement;
  public readonly data:Product;
  private updateHistory:boolean;

  constructor(container:HTMLElement, updateHistory:boolean=true) {
    this.container = container;
    this.updateHistory = updateHistory;
    this.variantSelector = container.querySelector('[name="id"]')!;
    this.elementData = container.querySelector<HTMLScriptElement>(SEL_ELEMENT_DATA)!;
    this.data = JSON.parse(this.elementData.innerHTML);

    this.variantSelector.addEventListener('change', this.onInputChanged);
  }

  public getVariantId():number | null {
    const value = this.variantSelector.value;
    if (!value || value === '') return null;
    const id = parseInt(value);
    return isNaN(id) ? null : id;
  }

  public getVariant():Variant | null {
    const id = this.getVariantId();
    if (id === null || isNaN(id) || !isFinite(id)) return null;
    return this.data.variants.find(v => v.id === id) || null;
  }

  public setVariantId(id:number | null) {
    if (id === null) {
      this.variantSelector.value = '';
      if(this.updateHistory) {
        queryReplaceParam('variant', null);
      }
      this.onVariantSelected();
      return;
    }
    
    if(isNaN(id) || !isFinite(id)) return;
    if(id.toString() == this.variantSelector.value) return;
    if(!this.data.variants.some(v => v.id === id)) return;

    const oldVariant = this.getVariant();

    this.variantSelector.value = id.toString();
    if(this.updateHistory) {
      queryReplaceParam('variant', id.toString());
    }
    this.onVariantSelected();

    const newVariant = this.getVariant();
    const evt = new EventVariantChanged(EVT_VARIANT_CHANGED, {
      bubbles: true,
      cancelable: false,
      detail: {
        oldVariant, newVariant, variantSelector: this
      }
    });
    this.variantSelector.dispatchEvent(evt);
  }

  public getOptionValues() {
    const values:{ [key:string]:string[] } = {};

    this.data.options.forEach(option => values[option] = []);

    this.data.variants.forEach(variant => {
      variant.options.forEach((opt,i) => {
        const key = this.data.options[i];
        if(values[key].indexOf(opt) !== -1) return;
        values[key].push(opt);
      });
    });

    return values;
  }

  public setVariantOption(index:number, value:string) {
    const options = this.getOptionValues();
    const currentVariant = this.getVariant();

    
    const intended:string[] = [];

    for(let i = 0; i < this.data.options.length; i++) {
      let selection:string;
      if(i === index) {
        selection = value;
      } else {
        // If no current variant, use the first available option for other positions
        if (!currentVariant) {
          const optionValues = options[this.data.options[i]];
          selection = optionValues[0] || '';
        } else {
          selection = currentVariant.options[i];
        }
      }
      intended.push(selection);
    }

    // Try find variant
    const newVariant = this.data.variants.find(v => {
      return v.options.every((o,oi) => {
        if(oi >= this.data.options.length) return true;
        return intended[oi] === o;
      });
    });

    if(!newVariant) return null;

    this.setVariantId(newVariant.id);
    return newVariant;
  }

  private onInputChanged = (e:Event) => {
    const value = this.variantSelector.value;
    if (!value || value === '') {
      this.setVariantId(null);
    } else {
      this.setVariantId(parseInt(value));
    }
  }

  public onVariantSelected() {}

  public dispose() {
    this.variantSelector.removeEventListener('change', this.onInputChanged);
  }
}